import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { baseurl } from "../../config/url";
import { format } from "date-fns";
import * as XLSX from "xlsx";

const ITEMS_PER_PAGE = 10;

const safe = (v) => (v ? String(v).trim() : "");

const isValidUser = (item) => {
  return (
    safe(item?.userId?.name) &&
    safe(item?.userId?.email) &&
    safe(item?.userId?.mobile)
  );
};

const InterestedBuyer = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showInvalid, setShowInvalid] = useState(false);

  const [selected, setSelected] = useState(null); // ✅ modal

  const token = localStorage.getItem("token");

  // ---------- FETCH ----------
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(
          `${baseurl}/api/admin/interested-buyer`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const data = Array.isArray(res.data)
          ? res.data
          : res.data?.data || [];

        setRows(data);
      } catch (err) {
        setError("Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  // ---------- FILTER ----------
  const filtered = useMemo(() => {
    let list = [...rows];

    if (!showInvalid) {
      list = list.filter(isValidUser);
    }

    if (search.trim()) {
      const q = search.toLowerCase();

      list = list.filter((item) => {
        return (
          safe(item?.userId?.name).toLowerCase().includes(q) ||
          safe(item?.userId?.email).toLowerCase().includes(q) ||
          safe(item?.userId?.mobile).toLowerCase().includes(q)
        );
      });
    }

    return list;
  }, [rows, search, showInvalid]);

  // ---------- PAGINATION ----------
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const start = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentItems = filtered.slice(start, start + ITEMS_PER_PAGE);

  // =========================
  // ✅ EXCEL DOWNLOAD
  // =========================
  const downloadExcel = () => {
    if (!filtered.length) return;

    const data = filtered.map((item) => ({
      Name: safe(item?.userId?.name),
      Email: safe(item?.userId?.email),
      Phone: safe(item?.userId?.mobile),
      Product: safe(item?.productId?.name),
      Date: item?.createdAt
        ? format(new Date(item.createdAt), "dd MMM yyyy")
        : "N/A",
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "Interested Buyers");

    XLSX.writeFile(wb, `Interested_Buyers_${Date.now()}.xlsx`);
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">

        {/* HEADER */}
        <div className="flex justify-between items-center mb-5 flex-wrap gap-3">
          <h2 className="text-2xl font-bold">Interested Buyers</h2>

          <div className="flex gap-2 flex-wrap">
            <input
              placeholder="Search name/email/phone..."
              value={search}
              onChange={(e) => {
                setCurrentPage(1);
                setSearch(e.target.value);
              }}
              className="border px-3 py-2 rounded-lg"
            />

            <button
              onClick={() => setShowInvalid((p) => !p)}
              className={`px-4 py-2 rounded-lg ${
                showInvalid
                  ? "bg-yellow-500 text-white"
                  : "bg-gray-200"
              }`}
            >
              {showInvalid ? "Showing All" : "Valid Only"}
            </button>

            {/* ✅ DOWNLOAD BUTTON */}
            <button
              onClick={downloadExcel}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              ⬇ Download Excel
            </button>
          </div>
        </div>

        {/* TABLE */}
        {loading ? (
          <div className="p-6 bg-white rounded shadow">Loading...</div>
        ) : error ? (
          <div className="p-6 bg-red-50 text-red-600 rounded">{error}</div>
        ) : currentItems.length === 0 ? (
          <div className="p-6 bg-white rounded shadow">
            No data found
          </div>
        ) : (
          <>
            <div className="bg-white rounded-xl shadow border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-3">#</th>
                    <th className="p-3">Name</th>
                    <th className="p-3">Email</th>
                    <th className="p-3">Phone</th>
                    <th className="p-3">Product</th>
                    <th className="p-3">Date</th>
                  </tr>
                </thead>

                <tbody>
                  {currentItems.map((item, index) => (
                    <tr
                      key={item._id}
                      onClick={() => setSelected(item)}
                      className="border-t hover:bg-blue-50 cursor-pointer"
                    >
                      <td className="p-3">{start + index + 1}</td>
                      <td className="p-3 font-semibold">
                        {safe(item?.userId?.name) || "-"}
                      </td>
                      <td className="p-3">
                        {safe(item?.userId?.email) || "-"}
                      </td>
                      <td className="p-3">
                        {safe(item?.userId?.mobile) || "-"}
                      </td>
                      <td className="p-3">
                        {safe(item?.productId?.name) || "-"}
                      </td>
                      <td className="p-3">
                        {item?.createdAt
                          ? format(new Date(item.createdAt), "dd MMM yyyy")
                          : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* PAGINATION */}
            {totalPages > 1 && (
              <div className="flex justify-between mt-4">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                  className="px-4 py-2 bg-gray-200 rounded"
                >
                  Prev
                </button>

                <span>
                  Page {currentPage} / {totalPages}
                </span>

                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                  className="px-4 py-2 bg-gray-200 rounded"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {/* ===================== */}
        {/* ✅ CENTER MODAL CARD */}
        {/* ===================== */}
        {selected && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">

            <div
              className="absolute inset-0"
              onClick={() => setSelected(null)}
            />

            <div className="relative bg-white w-full max-w-md rounded-xl shadow-xl p-6">

              <div className="flex justify-between mb-4">
                <h3 className="text-lg font-bold">Buyer Details</h3>
                <button onClick={() => setSelected(null)}>✕</button>
              </div>

              <div className="space-y-3 text-sm">
                <div><b>Name:</b> {safe(selected?.userId?.name)}</div>
                <div><b>Email:</b> {safe(selected?.userId?.email)}</div>
                <div><b>Phone:</b> {safe(selected?.userId?.mobile)}</div>
                <div><b>Product:</b> {safe(selected?.productId?.name)}</div>
                <div>
                  <b>Date:</b>{" "}
                  {selected?.createdAt
                    ? format(new Date(selected.createdAt), "dd MMM yyyy")
                    : "N/A"}
                </div>
              </div>

            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InterestedBuyer;